/* /account - the Account tab: who is signed in and their pass, then everything about the account in groups, the way
   a phone's settings are laid out (applying, you and your plan, privacy and security, the planner's tools), the
   applications and tasks that came in by email or the extension, the language, and signing out. Home, Explore and
   Apply are the planner's (the app's navigation, AccountShell). */
import { useState } from 'react';
import { planById, WEB_ROUTES, type Application, type InboxResponse, type ProfileBundle, type Task } from '@pathabroad/core/browser';
import { useExtension } from '../app/extension';
import { useT } from '../app/i18n';
import { useMe, useSession } from '../app/session';
import { useErrorHandler, useLoad } from '../app/hooks';
import { Icon } from '../app/icons';
import { LanguagePicker, SignOutRow } from '../app/layout';
import { Link } from '../app/nav';
import { AiTag, Chip, LoadError, Loading, Row, Section, cx, type Tone } from '../app/ui';
import { useDocumentTitle } from '../app/hooks';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';
import { activePasses, currentPhase, type Phase } from '../lib/passes';
import { deviceKind } from '../lib/stores';
import type { MessageKey } from '../lib/i18n';

const PHASES: MessageKey[] = ['phase.0', 'phase.1', 'phase.2', 'phase.3'];

export const APP_TONE: Record<Application['status'], Tone> = {
  planned: 'gray', in_progress: 'blue', submitted: 'teal', decision_admitted: 'green', decision_denied: 'gray',
  decision_waitlisted: 'orange', decision_deferred: 'orange', accepted: 'green', declined: 'gray', withdrawn: 'gray',
};

/* the first letters of the name, for the round badge (two at most, in any script) */
const initialsOf = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => Array.from(w)[0]).join('').toUpperCase();

export function Account() {
  const { t, lang } = useT();
  const me = useMe();
  const { signOut } = useSession();
  const ext = useExtension();
  const [device] = useState(() => deviceKind());
  const apps = useLoad(s => api.get<{ applications: Application[] }>('/api/applications', { signal: s }).then(r => r.applications), []);
  const tasks = useLoad(s => api.get<{ tasks: Task[] }>('/api/tasks', { signal: s }).then(r => r.tasks), []);
  const profile = useLoad(s => api.get<ProfileBundle>('/api/profile', { signal: s }).catch(() => null), []);
  const inbox = useLoad(s => (me.plans.apply ? api.get<InboxResponse>('/api/inbox', { signal: s }).catch(() => null) : Promise.resolve(null)), [me.plans.apply]);

  const p = profile.data?.profile;
  const name = [p?.preferred_name || p?.given_name, p?.family_name].filter(Boolean).join(' ').trim();
  useDocumentTitle(t('nav.account'));
  const phase = currentPhase(apps.data ?? []);
  const passes = activePasses(me.plans.passes);
  const address = inbox.data?.address ?? null;
  const pass = passes[0];

  return (
    <>
      <header className="page-head">
        <h1 id="page-title" className="title" tabIndex={-1}>{t('nav.account')}</h1>
      </header>

      {/* who is signed in, their pass, and where they are on the four-stop path */}
      <section className="acard night" aria-labelledby="acard-name">
        <div className="acard__top">
          <span className="acard__avatar" aria-hidden="true">{name ? initialsOf(name) : <Icon.person />}</span>
          <div className="acard__who">
            <h2 id="acard-name" className="acard__name"><bdi>{name || t('acct.you')}</bdi></h2>
            <span className="acard__email"><bdi dir="ltr">{me.user.email}</bdi></span>
          </div>
          <Link to={WEB_ROUTES.plans} className="acard__plan">
            {pass ? <><Icon.pass />{t('acct.pass', { plan: planById(pass.plan).name[lang] })}</> : <>{t('acct.freePlan')}<Icon.chevron /></>}
          </Link>
        </div>
        <PathProgress phase={phase} />
        {pass?.valid_until && <p className="acard__fine">{t('acct.validUntil', { date: formatDate(pass.valid_until, lang) })}</p>}
      </section>

      <Section title={t('acct.groupApply')} id="applying">
        <ul className="group list list--icons">
          <li>
            <Row to={WEB_ROUTES.inbox} icon={<Icon.inbox />} tone="blue" title={t('acct.inbox')}
              sub={me.plans.apply ? (address ? <bdi dir="ltr" className="truncate">{address}</bdi> : t('acct.inboxNone')) : t('acct.withApply')} />
          </li>
          <li>
            <Row to={WEB_ROUTES.extension} icon={<Icon.puzzle />} tone="indigo" title={t('acct.extension')}
              end={!me.plans.apply ? t('acct.withApply') : ext.state === 'connected' ? t('acct.extensionOn') : device === 'chromium' ? t('acct.extensionAdd') : t('acct.extensionSetUp')} />
          </li>
          <li><Row to={WEB_ROUTES.reminders} icon={<Icon.bell />} tone="red" title={t('nav.reminders')} /></li>
        </ul>
      </Section>

      <Section title={t('acct.apps')} id="apps">
        {apps.status === 'loading' ? <Loading /> : apps.status === 'error' ? <LoadError message={apps.message} retry={apps.reload} /> :
          apps.data.length === 0 ? <p className="empty">{t('acct.appsEmpty')}</p> : (
            <ul className="group list">
              {apps.data.map(a => (
                <li key={a.id}>
                  <div className="row">
                    <span className="row__main">
                      {/* official university names stay as they are, in either language */}
                      <span className="row__title"><bdi>{a.school_name}</bdi></span>
                      {a.submitted_at && <span className="row__sub">{t('acct.submitted', { date: formatDate(a.submitted_at, lang) })}</span>}
                    </span>
                    <span className="row__end"><Chip tone={APP_TONE[a.status]}>{t(`app.${a.status}`)}</Chip></span>
                  </div>
                </li>
              ))}
            </ul>
          )}
      </Section>

      <Section title={t('acct.tasks')} id="tasks">
        {tasks.status === 'loading' ? <Loading /> : tasks.status === 'error' ? <LoadError message={tasks.message} retry={tasks.reload} /> :
          <TaskList tasks={tasks.data} />}
      </Section>

      <Section title={t('acct.groupYou')} id="you">
        <ul className="group list list--icons">
          <li><Row to={WEB_ROUTES.profile} icon={<Icon.person />} tone="blue" title={t('nav.profile')} sub={t('acct.profileSub')} /></li>
          <li><Row to={WEB_ROUTES.plans} icon={<Icon.pass />} tone="orange" title={t('nav.plans')} end={pass ? undefined : t('acct.seePlans')} /></li>
          <li><Row to={WEB_ROUTES.family} icon={<Icon.people />} tone="teal" title={t('nav.family')} sub={t('acct.familySub')} /></li>
          {me.age === 'minor' && (
            <li><Row icon={<Icon.people />} tone="teal" title={t('acct.consent')} end={<Chip tone="green">{t('acct.consentGranted')}</Chip>} /></li>
          )}
        </ul>
      </Section>

      <Section title={t('acct.groupSafety')} id="security" foot={!me.mfa.enrolled ? t('acct.mfaNudge') : undefined}>
        <ul className="group list list--icons">
          <li>
            <Row to={WEB_ROUTES.security} icon={<Icon.shield />} tone="green" title={t('nav.security')}
              end={<Chip tone={me.mfa.enrolled ? 'green' : 'orange'}>{t('acct.mfaShort')} {me.mfa.enrolled ? t('common.on') : t('common.off')}</Chip>} />
          </li>
          <li><Row to={WEB_ROUTES.data} icon={<Icon.archive />} tone="gray" title={t('nav.data')} sub={t('acct.dataSub')} /></li>
        </ul>
      </Section>

      <Section title={t('acct.groupPlanner')} id="planner">
        <ul className="group list list--icons">
          <li><Row href={`${WEB_ROUTES.planner}#more`} icon={<Icon.map />} tone="teal" title={t('acct.tools')} sub={t('acct.toolsSub')} /></li>
          <li className="acct-lang"><div className="row"><span className="row__main"><span className="row__title">{t('lang.label')}</span></span><span className="row__end"><LanguagePicker /></span></div></li>
        </ul>
      </Section>

      <ul className="group list acct-out">
        <li><SignOutRow onClick={() => void signOut()} label={t('nav.signout')} /></li>
      </ul>
      <p className="acct-legal"><Link to={WEB_ROUTES.privacy}>{t('footer.privacy')}</Link><span aria-hidden="true"> · </span><Link to={WEB_ROUTES.terms}>{t('footer.terms')}</Link></p>
    </>
  );
}

/* the four stops as a compact route; the current one is marked for screen readers too */
function PathProgress({ phase }: { phase: Phase }) {
  const { t } = useT();
  return (
    <ol className="path" aria-label={t('phase.summary', { n: phase + 1, name: t(PHASES[phase]) })}>
      {PHASES.map((k, i) => (
        <li key={k} className={cx(i < phase && 'is-done', i === phase && 'is-current')} aria-current={i === phase ? 'step' : undefined}>
          <span className="path__dot" aria-hidden="true" />
          <span className="path__label">{t(k)}{i < phase && <span className="sr-only"> ({t('phase.done')})</span>}{i === phase && <span className="sr-only"> ({t('phase.here')})</span>}</span>
        </li>
      ))}
    </ol>
  );
}

function TaskList({ tasks }: { tasks: Task[] }) {
  const { t, lang } = useT();
  const handle = useErrorHandler();
  /* open tasks; ones ticked here stay visible (struck through) so they can be unticked */
  const [list, setList] = useState(() => tasks.filter(x => x.status === 'open'));
  const [error, setError] = useState<{ id: string; message: string } | null>(null);
  const toggle = async (task: Task, done: boolean) => {
    const status = done ? 'done' : 'open';
    setError(null);
    setList(l => l.map(x => (x.id === task.id ? { ...x, status } : x)));
    try {
      await api.patch(`/api/tasks/${encodeURIComponent(task.id)}`, { status });
    } catch (e) {
      setList(l => l.map(x => (x.id === task.id ? { ...x, status: task.status } : x)));
      const msg = handle(e);
      if (msg) setError({ id: task.id, message: t('task.error') });
    }
  };
  if (!list.length) return <p className="empty">{t('acct.tasksEmpty')}</p>;
  return (
    <ul className="group list">
      {list.map(task => (
        <li key={task.id}>
          <div className={cx('task', task.status === 'done' && 'task--done')}>
            <input type="checkbox" id={`task-${task.id}`} checked={task.status === 'done'} onChange={e => void toggle(task, e.target.checked)}
              aria-describedby={error?.id === task.id ? `task-${task.id}-err` : undefined} />
            <div className="task__main">
              <label htmlFor={`task-${task.id}`} className="task__title">{task.title}</label>
              <span className="task__meta">
                {task.due_date && <span>{t('acct.due', { date: formatDate(task.due_date, lang) })}</span>}
                {task.source === 'ai' && <AiTag full={t('task.aiFull')} />}
                {task.source === 'email' && <span>{t('task.email')}</span>}
              </span>
              {error?.id === task.id && <p className="field-error" id={`task-${task.id}-err`} role="alert"><Icon.alert />{error.message}</p>}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
